// @ts-check

import { getLocale } from '../locales';
import { connectStream } from '../stream';

import {
  fetchAnnouncements,
  updateAnnouncements,
  updateReaction as updateAnnouncementsReaction,
  deleteAnnouncement,
} from './announcements';
import { updateConversations } from './conversations';
import {
  processNewNotificationForGroups,
  refreshStaleNotificationGroups,
  pollRecentNotifications as pollRecentGroupNotifications,
} from './notification_groups';
import { updateNotifications } from './notifications';
import { updateStatus } from './statuses';
import {
  updateTimeline,
  deleteFromTimelines,
  expandHomeTimeline,
  connectTimeline,
  disconnectTimeline,
  fillHomeTimelineGaps,
  fillPublicTimelineGaps,
  fillCommunityTimelineGaps,
  fillListTimelineGaps,
} from './timelines';
import {
  receiveDmReadReceipt,
  receiveDmReaction,
  receiveDmDeleted,
  receiveDmTyping,
  handleDmEdited,
  fetchDirectMessages,
  receiveConversationDeleted,
} from './direct_messages';
import {
  GROUP_CHAT_DELETE_SUCCESS,
  GROUP_CHAT_MESSAGE_DELETE_SUCCESS,
  receiveGroupChatMessage,
  receiveGroupMessageEdited,
  receiveGroupMembersAdded,
  receiveGroupMembersRemoved,
  fetchUnreadGroupChatCount,
  receiveGroupReadReceipt,
  receiveGroupChatReaction,
  receiveGcTyping,
} from './group_chats';
import { me } from '../initial_state';

/**
 * @param {number} max
 * @returns {number}
 */
const randomUpTo = (max) => Math.floor(Math.random() * Math.floor(max));

/**
 * @typedef {import('mastodon/store').AppDispatch} Dispatch
 * @typedef {import('mastodon/store').GetState} GetState
 * @typedef {import('redux').UnknownAction} UnknownAction
 * @typedef {function(Dispatch, GetState): Promise<void>} FallbackFunction
 */

/**
 * @param {string} timelineId
 * @param {string} channelName
 * @param {Object.<string, string>} params
 * @param {Object} options
 * @param {FallbackFunction} [options.fallback]
 * @param {function(): UnknownAction} [options.fillGaps]
 * @param {function(object): boolean} [options.accept]
 * @returns {function(): void}
 */
export const connectTimelineStream = (
  timelineId,
  channelName,
  params = {},
  options = {},
) => {
  const { messages } = getLocale();

  // Public streams are currently not returning personalized quote policies
  const bogusQuotePolicy =
    channelName.startsWith('public') || channelName.startsWith('hashtag');

  return connectStream(channelName, params, (dispatch, getState) => {
    // @ts-ignore
    const locale = getState().getIn(['meta', 'locale']);

    // @ts-expect-error
    let pollingId;

    /**
     * @param {FallbackFunction} fallback
     */

    const useFallback = async (fallback) => {
      await fallback(dispatch, getState);
      // eslint-disable-next-line react-hooks/rules-of-hooks -- this is not a react hook
      pollingId = setTimeout(
        () => useFallback(fallback),
        20000 + randomUpTo(20000),
      );
    };

    return {
      onConnect() {
        dispatch(connectTimeline(timelineId));

        // @ts-expect-error
        if (pollingId) {
          // @ts-ignore
          clearTimeout(pollingId);
          pollingId = null;
        }

        if (options.fillGaps) {
          dispatch(options.fillGaps());
        }
      },

      onDisconnect() {
        dispatch(disconnectTimeline({ timeline: timelineId }));

        if (options.fallback) {
          pollingId = setTimeout(
            // @ts-expect-error
            () => useFallback(options.fallback),
            randomUpTo(40000),
          );
        }
      },

      onReceive(data) {
        switch (data.event) {
          case 'update':
            dispatch(
              // @ts-expect-error
              updateTimeline(timelineId, JSON.parse(data.payload), {
                // @ts-expect-error
                accept: options.accept,
                bogusQuotePolicy,
              }),
            );
            break;
          case 'status.update':
            dispatch(
              // @ts-expect-error
              updateStatus(JSON.parse(data.payload), { bogusQuotePolicy }),
            );
            break;
          case 'delete':
            dispatch(deleteFromTimelines(data.payload));
            break;
          case 'notification': {
            // @ts-expect-error
            const notificationJSON = JSON.parse(data.payload);
            dispatch(updateNotifications(notificationJSON, messages, locale));
            // TODO: remove this once the groups feature replaces the previous one
            dispatch(processNewNotificationForGroups(notificationJSON));
            break;
          }
          case 'notifications_merged': {
            dispatch(refreshStaleNotificationGroups());
            break;
          }
          case 'conversation':
            // @ts-expect-error
            dispatch(updateConversations(JSON.parse(data.payload)));
            break;
          case 'announcement':
            // @ts-expect-error
            dispatch(updateAnnouncements(JSON.parse(data.payload)));
            break;
          case 'announcement.reaction':
            // @ts-expect-error
            dispatch(updateAnnouncementsReaction(JSON.parse(data.payload)));
            break;
          case 'announcement.delete':
            dispatch(deleteAnnouncement(data.payload));
            break;
        }
      },
    };
  });
};

/**
 * @param {Dispatch} dispatch
 */
async function refreshHomeTimelineAndNotification(dispatch) {
  await dispatch(expandHomeTimeline({ maxId: undefined }));

  // TODO: polling for merged notifications
  try {
    await dispatch(pollRecentGroupNotifications());
  } catch {
    // TODO
  }

  await dispatch(fetchAnnouncements());
}

/**
 * @returns {function(): void}
 */
export const connectUserStream = () =>
  connectTimelineStream(
    'home',
    'user',
    {},
    {
      fallback: refreshHomeTimelineAndNotification,
      // @ts-expect-error
      fillGaps: fillHomeTimelineGaps,
    },
  );

/**
 * @param {Object} options
 * @param {boolean} [options.onlyMedia]
 * @returns {function(): void}
 */
export const connectCommunityStream = ({ onlyMedia } = {}) =>
  connectTimelineStream(
    `community${onlyMedia ? ':media' : ''}`,
    `public:local${onlyMedia ? ':media' : ''}`,
    {},
    {
      // @ts-expect-error
      fillGaps: () => fillCommunityTimelineGaps({ onlyMedia }),
    },
  );

/**
 * @param {Object} options
 * @param {boolean} [options.onlyMedia]
 * @param {boolean} [options.onlyRemote]
 * @returns {function(): void}
 */
export const connectPublicStream = ({ onlyMedia, onlyRemote } = {}) =>
  connectTimelineStream(
    `public${onlyRemote ? ':remote' : ''}${onlyMedia ? ':media' : ''}`,
    `public${onlyRemote ? ':remote' : ''}${onlyMedia ? ':media' : ''}`,
    {},
    {
      // @ts-expect-error
      fillGaps: () => fillPublicTimelineGaps({ onlyMedia, onlyRemote }),
    },
  );

/**
 * @param {string} columnId
 * @param {string} tagName
 * @param {boolean} onlyLocal
 * @param {function(object): boolean} accept
 * @returns {function(): void}
 */
export const connectHashtagStream = (columnId, tagName, onlyLocal, accept) =>
  connectTimelineStream(
    `hashtag:${columnId}${onlyLocal ? ':local' : ''}`,
    `hashtag${onlyLocal ? ':local' : ''}`,
    { tag: tagName },
    { accept },
  );

/**
 * @returns {function(): void}
 */
export const connectDirectStream = () =>
  connectTimelineStream('direct', 'direct');

/**
 * @param {string} listId
 * @returns {function(): void}
 */
export const connectListStream = (listId) =>
  connectTimelineStream(
    `list:${listId}`,
    'list',
    { list: listId },
    {
      // @ts-expect-error
      fillGaps: () => fillListTimelineGaps(listId),
    },
  );

/**
 * Single per-user stream that receives all group chat events:
 *   group_chat_message  — new message in any group the user belongs to
 *   group_chat_deleted  — a group the user was in was deleted
 *   members_removed     — someone (possibly the user) was removed from a group
 *
 * @returns {function(): void}
 */
export const connectGroupChatStream = (onConnect) =>
  connectStream('group_chat', {}, (dispatch) => ({
    onConnect() {
      // Socket (re)connect: streaming-only mutations (reactions, edits,
      // receipts on already-loaded rows) published while we were down are
      // lost — let the page refetch so they converge without a reload.
      if (typeof onConnect === 'function') onConnect();
    },
    onDisconnect() {},
    onReceive(data) {
      try {
        // @ts-ignore
        const payload = JSON.parse(data.payload);
        switch (data.event) {
          case 'group_chat_message':
            dispatch(receiveGroupChatMessage(payload));
            dispatch(fetchUnreadGroupChatCount());
            break;
          case 'group_chat_deleted':
            dispatch({
              type: GROUP_CHAT_DELETE_SUCCESS,
              groupChatId: payload.id,
            });
            break;
          case 'group_read_receipt':
            dispatch(receiveGroupReadReceipt(payload));
            break;
          case 'group_reaction':
            // @ts-ignore
            dispatch(receiveGroupChatReaction(payload, String(me)));
            break;
          case 'group_message_deleted':
            dispatch({
              type: GROUP_CHAT_MESSAGE_DELETE_SUCCESS,
              messageId: payload.message_id,
            });
            break;
          case 'gc_typing':
            // @ts-ignore
            if (String(payload.account_id) !== String(me)) {
              dispatch(receiveGcTyping(payload));
            }
            break;
          case 'group_message_edited':
            dispatch(receiveGroupMessageEdited(payload));
            break;
          case 'members_added':
            dispatch(receiveGroupMembersAdded(payload));
            break;
          case 'members_removed':
            dispatch(receiveGroupMembersRemoved(payload));
            break;
        }
      } catch {
        // ignore malformed events
      }
    },
  }));

/**
 * @param {(() => void)} [onConnect] fired on every socket connect/reconnect
 * @returns {function(): void}
 */
export const connectDmStream = (onConnect) =>
  connectStream('direct', {}, (dispatch) => ({
    onConnect() {
      // DRH-4: fire the optional connect/reconnect hook (index.tsx uses it to
      // re-assert outstanding decrypt gaps the moment the socket comes back, so
      // recovery converges without waiting for the next timed poll sweep).
      if (typeof onConnect === 'function') onConnect();
    },
    onDisconnect() {},
    onReceive(data) {
      try {
        // @ts-ignore
        const payload = JSON.parse(data.payload);
        switch (data.event) {
          case 'dm_received':
            // Refresh the inbox so the new conversation/message appears immediately.
            dispatch(fetchDirectMessages());
            break;
          case 'dm_read_receipt':
            dispatch(receiveDmReadReceipt(payload));
            break;
          case 'dm_reaction':
            // @ts-ignore
            dispatch(receiveDmReaction(payload, String(me)));
            break;
          case 'dm_deleted':
            dispatch(receiveDmDeleted(payload));
            break;
          case 'dm_typing':
            // @ts-ignore
            dispatch(receiveDmTyping(payload));
            break;
          case 'dm_edited':
            dispatch(handleDmEdited(payload));
            break;
          case 'dm_conversation_deleted':
            // @ts-ignore
            dispatch(receiveConversationDeleted(payload, String(me)));
            break;
        }
      } catch {
        // ignore malformed events
      }
    },
  }));
